import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/hooks/use-theme";
import { Header } from "@/components/Header";
import { ScrollToTop } from "@/components/ScrollToTop";

import { LegacyRedirectGate } from "@/components/LegacyRedirect";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { installWhatsAppTracking } from "@/lib/analytics";
import { toOgJpg } from "@/lib/og-jpg";
import ogImageAsset from "@/assets/OP2P-CRM-e-Agentes-de-IA-whatsapp-instagram-og.webp.asset.json";
import brandLogoAsset from "@/assets/OP2P-CRM-e-Agentes-de-IA-logotipo-colorido-2026.webp.asset.json";

import appCss from "../styles.css?url";

const OG_IMAGE_URL = `https://op2p.ai${toOgJpg(ogImageAsset.url)}`;
const BRAND_LOGO_URL = `https://op2p.ai${brandLogoAsset.url}`;

const ORGANIZATION_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "OP2P",
  alternateName: "OP2P CRM e Agentes de IA",
  url: "https://op2p.ai",
  logo: {
    "@type": "ImageObject",
    url: BRAND_LOGO_URL,
    caption: "Logotipo OP2P CRM e Agentes de IA",
  },
  image: {
    "@type": "ImageObject",
    url: OG_IMAGE_URL,
    caption: "OP2P — CRM com WhatsApp, Instagram e Agentes de IA",
  },
  description:
    "OP2P é um CRM que reúne WhatsApp e Instagram e usa Agentes de IA para atender, qualificar, fazer follow-up e organizar a operação comercial.",
  foundingDate: "2004",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-11-96324-2299",
    contactType: "customer service",
    availableLanguage: ["Portuguese"],
  },
  sameAs: [
    "https://www.instagram.com/op2p.ai",
    "https://www.facebook.com/op2pai",
    "https://www.tiktok.com/@op2p.ai",
    "https://www.youtube.com/@OP2Pai",
    "https://www.linkedin.com/company/op2p/",
  ],
});

const WEBSITE_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "OP2P CRM e Agentes de IA",
  url: "https://op2p.ai",
  inLanguage: "pt-BR",
});

const SOFTWARE_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "OP2P",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "CRM e Sistema Multiagentes de IA",
  operatingSystem: "Web",
  description:
    "OP2P é um CRM inteligente que reúne WhatsApp e Instagram. Agentes de IA atendem, qualificam, fazem follow-up e organizam oportunidades no funil comercial.",
  url: "https://op2p.ai",
  provider: {
    "@type": "Organization",
    name: "OP2P",
    url: "https://op2p.ai",
    logo: BRAND_LOGO_URL,
    sameAs: [
      "https://www.instagram.com/op2p.ai",
      "https://www.facebook.com/op2pai",
      "https://www.tiktok.com/@op2p.ai",
      "https://www.youtube.com/@OP2Pai",
      "https://www.linkedin.com/company/op2p/",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+55-11-96324-2299",
      contactType: "sales",
      availableLanguage: "Portuguese",
    },
  },
  featureList: [
    "CRM inteligente nativo movido por IA",
    "Sistema multiagentes: comercial, suporte, administrativo e pós-venda",
    "Agentes de IA para WhatsApp e Instagram",
    "Caixa de entrada unificada para conversas dos dois canais",
    "Clone de Voz com IA",
    "Atendimento 24/7 humanizado",
    "Mais de 20 agentes prontos",
    "Implantação assistida pela equipe OP2P",
    "Dashboard em tempo real",
    "Disparo em massa",
    "Agendamento automatizado",
    "Live Chat com IA",
    "Gestão de contatos inteligente",
  ],
});


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        name: "keywords",
        content:
           "crm com inteligência artificial superinteligência — SI, crm whatsapp e instagram, automação instagram, automação whatsapp, agentes de ia, atendimento omnichannel, gestão comercial com ia, follow-up automático, OP2P CRM",
      },
      { name: "author", content: "OP2P" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "google-site-verification", content: "d6zS5zwlgHB051Zi9R-nNq8sFXiiHS9dSAXNge-dm50" },
      { name: "language", content: "Portuguese" },
      {
        property: "og:image:alt",
         content: "OP2P — CRM com WhatsApp, Instagram e Agentes de IA",
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:site_name", content: "OP2P CRM e Agentes de IA" },
      {
        name: "twitter:image:alt",
         content: "OP2P — CRM com WhatsApp, Instagram e Agentes de IA",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico?v=5", sizes: "any" },
      { rel: "icon", href: "/OP2P-CRM-e-Agentes-de-IA-icone.png?v=5", type: "image/png" },
      { rel: "shortcut icon", href: "/favicon.ico?v=5" },
      { rel: "apple-touch-icon", href: "/OP2P-CRM-e-Agentes-de-IA-icone.png?v=5" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@500;600;700;800&display=swap" },
      { rel: "dns-prefetch", href: "https://wa.me" },
      { rel: "dns-prefetch", href: "https://calendly.com" },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      { rel: "preload", as: "image", href: "/OP2P-CRM-e-Agentes-de-IA-icone.png?v=5" },
    ],
    scripts: [
      { src: "https://www.googletagmanager.com/gtag/js?id=G-K71J8DH4Z5", async: true },
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-K71J8DH4Z5');",
      },
      {
        children:
          "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','1092362348355215',{},{agent:'lovable'});fbq('track','PageView');",
      },
      {
        children:
          '(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","z1rcjhxblt");',
      },
      { type: "application/ld+json", children: SOFTWARE_JSONLD },
      { type: "application/ld+json", children: ORGANIZATION_JSONLD },
      { type: "application/ld+json", children: WEBSITE_JSONLD },

    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR" className="light" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => installWhatsAppTracking(), []);
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <Header />
            <LegacyRedirectGate>
              <Outlet />
            </LegacyRedirectGate>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const normalizedError = error instanceof Error ? error : new Error(String(error));

  console.error(normalizedError);

  useEffect(() => {
    reportLovableError(normalizedError, { boundary: "tanstack_root_error_component" });
  }, [normalizedError]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold">Esta página não carregou</h1>
        <p className="text-muted-foreground">
          Algo deu errado do nosso lado. Você pode tentar novamente ou voltar para a página inicial.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Tentar novamente
          </button>
          <a className="px-4 py-2 rounded-md border border-border" href="/">
            Página inicial
          </a>
        </div>
      </div>
    </div>
  );
}
